import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { CATEGORIES } from "@/lib/data";
import { api, getResults } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { levelText } from "@/lib/scoring";

function renderMarkdown(text) {
  return text
    .split(/\n\n+/)
    .map((block, i) => {
      if (block.startsWith("## ")) {
        return <h3 key={i} className="text-xl font-semibold text-white mt-6 mb-3">{block.replace("## ", "")}</h3>;
      }
      return <p key={i} className="text-neutral-300 leading-relaxed">{block}</p>;
    });
}

export default function Report() {
  const nav = useNavigate();
  const r = getResults();
  const { user } = useAuth();
  const [aiText, setAiText] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!r) return;
    (async () => {
      try {
        const { data } = await api.post("/report", {
          first_name: user?.first_name,
          general_score: r.general,
          scores: r.scores,
          strongest: r.strongest,
          development: r.development,
        });
        setAiText(data.report);
      } catch {
        setErr("Rapor üretilemedi. Lütfen biraz sonra tekrar dene.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (!r) {
    return (
      <div className="text-center py-24">
        <p className="text-neutral-400">Henüz test sonucun yok.</p>
        <button onClick={() => nav("/test")} className="btn-primary mt-6">Testi Başlat</button>
      </div>
    );
  }

  return (
    <div data-testid="report-page">
      <div className="flex items-center gap-3 text-xs uppercase tracking-[0.15em] text-neutral-500">
        <Sparkles size={14} /> AI Destekli Kişisel Rapor
      </div>
      <h1 className="mt-3 text-5xl md:text-6xl font-semibold tracking-tight h-gradient">Kişisel Raporun</h1>
      <p className="mt-5 text-neutral-400 max-w-2xl">Sonuçlarına göre seni en çok yavaşlatan alanları ve genel profilini analiz ettik.</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-12">
        <div className="glass p-7">
          <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Skor Özeti</div>
          <div className="mt-3 text-6xl font-bold h-gradient">{r.general}<span className="text-xl text-neutral-500 align-top ml-1">/100</span></div>
          <div className="mt-2 text-neutral-400 text-sm">{levelText(r.general)}</div>
        </div>
        <div className="lg:col-span-2 glass p-7">
          <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-3">Güçlü Yönlerin</div>
          <div className="flex flex-wrap gap-2 mb-4">
            {r.strongest.map((s) => <span key={s} className="px-3 py-1.5 rounded-full border border-white/15 text-sm bg-white/5">{s}</span>)}
          </div>
          <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 mb-3">Gelişim Alanların</div>
          <div className="flex flex-wrap gap-2">
            {r.development.map((s) => <span key={s} className="px-3 py-1.5 rounded-full border border-white/15 text-sm bg-white/5">{s}</span>)}
          </div>
        </div>
      </div>

      <div className="glass mt-6 p-8" data-testid="ai-report-block">
        {loading && <div className="text-neutral-500 text-sm">Yapay zeka raporunu hazırlıyor...</div>}
        {err && <div className="text-red-300/80 text-sm">{err}</div>}
        {!loading && aiText && <div className="prose-life">{renderMarkdown(aiText)}</div>}
      </div>

      <div className="mt-10 flex justify-end">
        <button onClick={() => nav("/oneriler")} className="btn-primary inline-flex items-center gap-2" data-testid="view-resources-btn">
          Önerileri İncele <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}
